import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Cloud, ScanText, WifiOff, ArrowRight } from 'lucide-react';

// Data handling summary for the law-firm page. Must stay consistent with
// src/pages/AIDisclosuresPage.js ("What data is sent") and the
// /law-firms-confidentiality brief. Ground truth (app v8.63):
//   - cloud AI via Sortio's API is the default (OpenAI is the only cloud AI
//     provider; a new one must also go on /sub-processors and in the Privacy
//     Policy before it gets data); filenames, folder paths and the
//     instruction are sent
//   - BYOK still sends the same data to a cloud provider (the firm's own
//     contract), so it is NOT an option for firms that can't use a cloud AI
//   - Analyze contents (off by default) adds an excerpt of each file, about
//     4 KB; it is one global setting and stays on until turned off
//   - OCR runs on the device (macOS Vision, bundled Tesseract on Windows, no
//     OCR on Linux); the scanned image is never sent to a model
//   - Offline Mode (Ollama or LM Studio) keeps AI requests on the machine
// Do not claim local or private processing as the default, or that no data
// at all goes out.
const CARDS = [
  {
    icon: Cloud,
    title: 'By default: cloud AI models',
    body:
      "Sortio uses OpenAI's cloud AI models through Sortio's API. They receive filenames, folder paths and your instruction, and, with Analyze contents on, text from each document, up to an excerpt of about 4 KB. Or use your firm's own OpenAI or Anthropic API key (BYOK), so requests go to that provider under your firm's contract.",
  },
  {
    icon: ScanText,
    title: 'Scans are read on your computer',
    body:
      'OCR runs on your Mac or Windows PC. With Analyze contents on, the recognized text excerpt is sent, not the scanned image. Analyze contents is off until you turn it on, and then stays on until you turn it off in Settings.',
  },
  {
    icon: WifiOff,
    title: "Offline Mode, for firms that can't use a cloud AI",
    body:
      "Offline Mode runs the AI on your own computer with Ollama or LM Studio, so filenames and document text aren't sent to an AI provider. It needs a local model installed, and results can differ from the cloud model.",
  },
];

const DataHandling = () => {
  return (
    <section id="privacy" className="my-16 sm:my-20 scroll-mt-24">
      <div className="bg-gradient-to-br from-apple-800/60 to-apple-900/60 backdrop-blur-md rounded-3xl border border-apple-700/40 p-6 sm:p-12 relative overflow-hidden">
        <div className="absolute -left-32 -top-32 w-72 h-72 rounded-full bg-sortio-primary/10 blur-[100px]" />
        <div className="absolute -right-32 -bottom-32 w-72 h-72 rounded-full bg-sortio-secondary/10 blur-[100px]" />

        <div className="relative z-10">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 rounded-full bg-sortio-primary/15 border border-sortio-primary/30 flex items-center justify-center mr-4 flex-shrink-0">
              <ShieldCheck className="text-sortio-primary" size={22} aria-hidden="true" />
            </div>
            <span className="text-xs font-medium uppercase tracking-wider text-sortio-primary">
              Confidentiality
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl font-semibold mb-6 tracking-tight text-apple-50 max-w-3xl">
            What gets sent, and what doesn&apos;t
          </h2>

          <p className="max-w-3xl text-apple-200 leading-relaxed">
            You choose the AI that reads your documents, and you choose whether it reads their
            contents at all. Either way, a sort doesn&apos;t move anything until you preview it and
            click Apply.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mt-8 sm:mt-10">
            {CARDS.map((card, i) => {
              const Icon = card.icon;
              return (
                <div key={i} className="bg-apple-800/40 rounded-2xl border border-apple-700/50 p-6">
                  <div className="flex items-center mb-3">
                    <Icon className="text-sortio-primary mr-2 flex-shrink-0" size={18} aria-hidden="true" />
                    <h3 className="text-base font-medium text-apple-50">{card.title}</h3>
                  </div>
                  <p className="text-sm text-apple-200 leading-relaxed">{card.body}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-sm text-apple-300 max-w-2xl">
              Data is encrypted in transit (TLS 1.2+) and at rest. Cloud sort requests are kept for
              up to 30 days for service operation and debugging.
            </p>
            <Link
              to="/law-firms-confidentiality"
              className="inline-flex items-center text-sortio-primary hover:underline font-medium text-sm flex-shrink-0"
            >
              Read the confidentiality brief <ArrowRight className="ml-1" size={16} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DataHandling;
